import {
  BookingTransactionDataInput,
  PaymentMethod,
  useCreateBookingMutation,
  useCreateBookingTransactionMutation,
} from 'drycut-graphql';
import { Button } from 'ui-components';
import { useState } from 'react';
import DatatransErrorModal from './DatatransErrorModal';
import Lightbox from 'react-datatrans-light-box';
import { useNavigate } from 'react-router-dom';
import { env } from 'navigation';

type Props = {
  data: BookingTransactionDataInput;
  amount: number;
} & React.ComponentProps<typeof Button>;

export function BookingPayment({ amount, data, ...props }: Props): JSX.Element {
  const [createPayment, { loading: creatingPayment }] = useCreateBookingTransactionMutation();

  const [createBooking, { loading: creatingBooking }] = useCreateBookingMutation();
  const [transactionError, setTransactionError] = useState<string>();
  const [datatransId, setDatatransId] = useState<string>();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    let paymentMethod: PaymentMethod = PaymentMethod.Unk;

    if (data.manual) {
      paymentMethod = PaymentMethod.Direct;
    }

    // A coupon code was used so the amount to pay is 0, we should not show the Datatrans lightbox
    if (amount === 0) {
      paymentMethod = PaymentMethod.Skip;
    }

    // If it's a manual booking or the amount is 0, we skip the payment step
    const skipPayment =
      paymentMethod === PaymentMethod.Skip || paymentMethod === PaymentMethod.Direct;

    const response = await createPayment({
      variables: { input: { amount, paymentMethod, data } },
    });

    const payload = response.data?.createBookingTransaction;

    if (payload?.errors && payload?.errors?.length > 0) {
      setTransactionError(payload.errors[0].message);
    } else {
      setTransactionError('');
      const transaction = payload?.paymentTransaction;

      // If it's a skip payment, we can create the booking right away and redirect the user to the
      // booking details when it's done. Otherwise the redirect will be a hard one from the
      // backend, after Datatrans has done its thing.
      if (payload && skipPayment && transaction?.id) {
        const booking = await createBooking({
          variables: { input: { transactionId: transaction?.id } },
        });

        const uid = booking.data?.createBooking?.booking?.uid;

        navigate(`/booking/${uid}`);
      }

      if (transaction?.datatransId) {
        setDatatransId(transaction.datatransId);
      }
    }
  };

  return (
    <>
      <Button
        type="button"
        data-mode={env}
        loading={creatingPayment || creatingBooking || !!datatransId}
        onClick={handleSubmit}
        {...props}
      >
        {data.manual ? 'Buchen' : 'Bezahlen'}
      </Button>

      {datatransId && (
        <Lightbox
          transactionId={datatransId}
          production={env === 'production'}
          onCancelled={() => setDatatransId('')}
        />
      )}

      <DatatransErrorModal
        open={!!transactionError}
        error={transactionError || ''}
        onClose={() => setTransactionError('')}
      />
    </>
  );
}
